import React, { useEffect } from "react";

import { Button, Alert, Loader } from "@partials";
import { AlertType } from "@constants";
import { getLocalization } from "@utilities";
import { usePagination } from "@hooks";

import { SearchListType as Props, SearchResultType } from "./types";
import { SEARCH_FORM_CLASSNAME } from "./constants";
import { ItemsList } from "./components";
import { PageLinks } from "./components/PageLinks";

const SearchList: React.FC<Props> = ({
  api,
  query,
  pagination: defaultPagination,
  localization: widgetConfigLocalization,
  cultureCode,
  pageLinks,
  pageSize,
  showBreadcrumbs,
  searchFolder,
  hideSegmentBreadcrumb,
}) => {
  const {
    hasNextItems,
    isLoadingMore,
    items,
    loadNewItemsWithQuery,
    loadNextItems,
    hasError,
    isLoading,
    query: currentQuery,
    localization: apiLocalization,
  } = usePagination({
    api,
    defaultQuery: query,
    formElement: document.querySelector(`.${SEARCH_FORM_CLASSNAME}`),
    formInputName: "query",
    defaultItems: [],
    defaultPagination,
    cultureCode,
    pageSize,
    searchFolder,
    hideSegmentBreadcrumb,
  });

  const shouldShowItems = items.length > 0 && currentQuery && isLoading === false;
  const shouldShowPageLink = items.length === 0 && isLoading === false;
  const noResults = items.length === 0 && currentQuery;
  const localization = { apiLocalization, widgetConfigLocalization };

  useEffect(() => {
    if (currentQuery) {
      window.history.replaceState(null, null, `?query=${currentQuery}`);
      loadNewItemsWithQuery(currentQuery);
    }
  }, []);

  const loadMore = () => {
    window.history.replaceState(null, null, `?query=${currentQuery}`);
    loadNextItems();
  };

  return (
    <div
      className={
        (shouldShowItems && !isLoading) ||
        hasError ||
        (noResults && !isLoading && !hasError) ||
        (hasNextItems && !isLoading)
          ? "container mb-12"
          : "container"
      }
    >
      <div className="row">
        <div className="col-24">{isLoading ? <Loader /> : null}</div>
        <div className="sm:col-14 sm:offset-1">
          {hasError && (
            <div className="mb-8">
              <Alert
                type={AlertType.Error}
                text={getLocalization(localization.apiLocalization, "search/ErrorGeneric")}
                textAttributes={{ role: "alert", "aria-live": "assertive" }}
              />
            </div>
          )}

          {noResults && !isLoading && !hasError ? (
            <div className="mb-8">
              <Alert
                type={AlertType.Error}
                text={getLocalization(localization.apiLocalization, "search/NoResults")}
                textAttributes={{ role: "alert", "aria-live": "assertive" }}
              />
            </div>
          ) : null}

          {shouldShowItems && !isLoading ? (
            <ItemsList
              items={items as SearchResultType[]}
              pagination={defaultPagination}
              apiLocalization={apiLocalization}
              title={getLocalization(localization.apiLocalization, "search/resultCountInj")}
              showBreadcrumbs={showBreadcrumbs}
            />
          ) : null}

          {shouldShowPageLink && (
            <PageLinks
              pageLinks={pageLinks}
              title={getLocalization(localization.widgetConfigLocalization, "search/oftentitle")}
            />
          )}
          {hasNextItems && !isLoading && shouldShowItems ? (
            <Button
              template="secondary"
              title={getLocalization(localization.apiLocalization, "search/loadMoreButton")}
              type="button"
              isLoading={isLoadingMore}
              onClick={loadMore}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SearchList;
